import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getProductStockStatus, isStockUnlimited } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';

interface ProductStockStatusIconProps {
  stock?: number | null;
  lowStockAlert?: number | null;
  isArchived?: boolean;
}

/** Affiche l'icône carton colorée selon le statut de stock du produit. */
export function ProductStockStatusIcon({
  stock,
  lowStockAlert,
  isArchived = false
}: ProductStockStatusIconProps) {
  const { t } = useTranslation();

  if (isArchived) {
    return (
      <span
        className="flex relative justify-center items-center rounded-full shadow-sm size-5 bg-muted text-muted-foreground"
        title={t('admin.shops.products.status.archived')}
      >
        <Package className="opacity-70 size-3" aria-hidden />
        <span className="flex absolute inset-0 justify-center items-center" aria-hidden>
          <span className="block w-4 h-px rounded-full rotate-45 bg-muted-foreground" />
        </span>
        <span className="sr-only">{t('admin.shops.products.status.archived')}</span>
      </span>
    );
  }

  const stockStatus = getProductStockStatus(stock, lowStockAlert);
  const indicator =
    stockStatus === 'out_of_stock'
      ? {
          className: 'bg-destructive text-destructive-foreground',
          label: t('admin.shops.products.stock.out')
        }
      : stockStatus === 'low_stock'
        ? {
            className: 'bg-orange-500 text-white',
            label: t('admin.shops.products.stock.low')
          }
        : {
            className: 'bg-green-600 text-white',
            label: isStockUnlimited(stock)
              ? t('admin.shops.products.stock.unlimited')
              : t('admin.shops.products.stock.available')
          };

  return (
    <span
      className={cn(
        'flex size-5 items-center justify-center rounded-full shadow-sm',
        indicator.className
      )}
      title={indicator.label}
    >
      <Package className="size-3" aria-hidden />
      <span className="sr-only">{indicator.label}</span>
    </span>
  );
}
